import * as SelectPrimitive from '@radix-ui/react-select';
import { clsx } from 'clsx';
import { ComponentProps, ComponentPropsWithRef, ReactNode, useId } from 'react';
import { CaretArrowDownIcon } from '../icons/react/caret-arrow-down.tsx';
import { CaretArrowUpIcon } from '../icons/react/caret-arrow-up.tsx';
import { CheckIcon } from '../icons/react/check.tsx';
import { XmarkCircleIcon } from '../icons/react/xmark-circle.tsx';
import { InputIconButton } from '../input/input-icon-button.tsx';
import { inputError, inputLabel, inputVariants } from '../input/input.css.ts';
import { space } from '../tokens.ts';
import {
  selectClearButton,
  selectContent,
  selectIcon,
  selectItem,
  selectItemIndicator,
  selectLabel,
  selectTrigger,
  selectTriggerAside,
  selectTriggerVariants,
  selectViewport,
  selectScrollButton,
} from './select.css.ts';

export const Select = ({
  ...props
}: ComponentProps<typeof SelectPrimitive.Root>) => (
  <SelectPrimitive.Root {...props} />
);

interface SelectTriggerProps extends Omit<
  ComponentPropsWithRef<typeof SelectPrimitive.Trigger>,
  'asChild'
> {
  variant?: 'outline' | 'ghost';
  placeholder?: string;
  errorMessage?: string;
  label?: string;
  triggerText?: string;
  onClear?: () => void;
  containerClassName?: string;
}

export const SelectTrigger = function SelectTriggerComponent({
  ref,
  variant = 'outline',
  placeholder,
  label,
  errorMessage,
  id,
  className,
  triggerText,
  onClear,
  containerClassName,
  ...props
}: SelectTriggerProps) {
  const reactId = useId();
  const selectId = id || reactId;

  const carretSize = variant === 'outline' ? 24 : 20;

  return (
    <div className={containerClassName}>
      {label && (
        <label htmlFor={selectId} className={inputLabel}>
          {label}
        </label>
      )}
      <SelectPrimitive.Trigger
        className={clsx(
          selectTrigger,
          selectTriggerVariants[variant],
          errorMessage && inputVariants.error,
          className,
        )}
        ref={ref}
        id={selectId}
        {...props}
      >
        <SelectPrimitive.Value data-select-value placeholder={placeholder}>
          {triggerText}
        </SelectPrimitive.Value>

        <div className={selectTriggerAside}>
          {onClear && (
            <InputIconButton
              className={selectClearButton}
              onPointerDown={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
              onKeyDown={(e) => {
                e.stopPropagation();
              }}
              onClick={onClear}
              asChild
            >
              <div>
                <XmarkCircleIcon />
              </div>
            </InputIconButton>
          )}
          <SelectPrimitive.Icon className={selectIcon} asChild>
            <CaretArrowDownIcon width={carretSize} height={carretSize} />
          </SelectPrimitive.Icon>
        </div>
      </SelectPrimitive.Trigger>
      {errorMessage && <p className={inputError}>{errorMessage}</p>}
    </div>
  );
};

type SelectSimpleItem = {
  value: string;
  content: ReactNode;
  disabled?: boolean;
};

type SelectGroupItem = {
  label: string;
  items: SelectSimpleItem[];
};

export type SelectItem = SelectSimpleItem | SelectGroupItem;

export interface SelectContentProps extends ComponentPropsWithRef<
  typeof SelectPrimitive.Content
> {
  items: SelectItem[];
}

export const SelectContent = function SelectTriggerComponent({
  ref,
  items,
  className,
  ...props
}: SelectContentProps) {
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        ref={ref}
        className={clsx(selectContent, className)}
        sideOffset={space.s4}
        {...props}
      >
        <SelectPrimitive.ScrollUpButton
          data-direction="up"
          className={selectScrollButton}
        >
          <CaretArrowUpIcon />
        </SelectPrimitive.ScrollUpButton>
        <SelectPrimitive.Viewport className={selectViewport}>
          {items.map((item, index) => {
            if ('items' in item) {
              return (
                <SelectPrimitive.Group key={index}>
                  <SelectPrimitive.Label className={selectLabel}>
                    {item.label}
                  </SelectPrimitive.Label>
                  {item.items.map((item, index) => (
                    <SelectItem
                      key={index}
                      value={item.value}
                      disabled={item.disabled}
                    >
                      {item.content}
                    </SelectItem>
                  ))}
                </SelectPrimitive.Group>
              );
            } else {
              return (
                <SelectItem
                  key={index}
                  value={item.value}
                  disabled={item.disabled}
                >
                  {item.content}
                </SelectItem>
              );
            }
          })}
        </SelectPrimitive.Viewport>
        <SelectPrimitive.ScrollDownButton
          data-direction="down"
          className={selectScrollButton}
        >
          <CaretArrowDownIcon />
        </SelectPrimitive.ScrollDownButton>
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  );
};

const SelectItem = function SelectItemComponent({
  ref,
  children,
  className,
  ...props
}: ComponentPropsWithRef<typeof SelectPrimitive.Item>) {
  return (
    <SelectPrimitive.Item
      className={clsx(selectItem, className)}
      {...props}
      ref={ref}
    >
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
      <SelectPrimitive.ItemIndicator className={selectItemIndicator}>
        <CheckIcon />
      </SelectPrimitive.ItemIndicator>
    </SelectPrimitive.Item>
  );
};
